import type { Jss, JssStyle } from 'jss';
import type { ColumnStructureEntity, SectionStructureEntity } from '@web/types';

import { websiteColorCss } from '@libs/themes/src/variants/website/utils/variables';
import { EFilerOverlay, EVerticalPosition } from '@common/enums';
import { encodeFilePreviewUrlWithResize } from '@components/sp-file-manager/src/helpers';

function getRgbaColor(color: string, opacity: number): string {
    let r, g, b;

    if (color.includes('rgb')) {
        const rgbArr = color.match(/\d+/g);
        r = rgbArr[0];
        g = rgbArr[1];
        b = rgbArr[2];
    } else {
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
    }

    return `rgba(${r},${g},${b},${opacity})`;
}

export function getStyles(
    column: ColumnStructureEntity,
    columnSection: SectionStructureEntity,
    isConstructor: boolean = false,
    isPreview: boolean = false,
    fileManagerCdnUrl: string,
): JssStyle {
    const { columnIndent } = columnSection;
    const { styles, mobileStyles, data, mobileData } = column;
    const verticalPositionMatrix = {
        [EVerticalPosition.T]: 'flex-start',
        [EVerticalPosition.M]: 'center',
        [EVerticalPosition.B]: 'flex-end',
    };

    const boxShadowValue = '0 0 12px 4px rgba(0, 0, 0, .08)';
    const borderValue = `${styles.borderWidth}px ` + styles.borderStyle + ` ${styles.borderColor}`;
    let colorOverflow = '';

    let columnWrapper: JssStyle = {
        height: '100%',
        position: 'relative',
        marginRight: columnIndent / 2,
        marginLeft: columnIndent / 2,
    };

    const borderRadiusStyles: JssStyle = {
        borderTopLeftRadius:
            styles?.borderTopLeftRadius || styles?.borderTopLeftRadius === 0
                ? `${styles?.borderTopLeftRadius}px`
                : `${styles?.borderRadius}px`,
        borderTopRightRadius:
            styles?.borderTopRightRadius || styles?.borderTopRightRadius === 0
                ? `${styles?.borderTopRightRadius}px`
                : `${styles?.borderRadius}px`,
        borderBottomRightRadius:
            styles?.borderBottomRightRadius || styles?.borderBottomRightRadius === 0
                ? `${styles?.borderBottomRightRadius}px`
                : `${styles?.borderRadius}px`,
        borderBottomLeftRadius:
            styles?.borderBottomLeftRadius || styles?.borderBottomLeftRadius === 0
                ? `${styles?.borderBottomLeftRadius}px`
                : `${styles?.borderRadius}px`,
    };

    const mobileBorderRadiusStyles: JssStyle = {
        borderTopLeftRadius:
            mobileStyles?.borderTopLeftRadius || mobileStyles?.borderTopLeftRadius === 0
                ? `${mobileStyles?.borderTopLeftRadius}px`
                : `${mobileStyles?.borderRadius}px`,
        borderTopRightRadius:
            mobileStyles?.borderTopRightRadius || mobileStyles?.borderTopRightRadius === 0
                ? `${mobileStyles?.borderTopRightRadius}px`
                : `${mobileStyles?.borderRadius}px`,
        borderBottomRightRadius:
            mobileStyles?.borderBottomRightRadius || mobileStyles?.borderBottomRightRadius === 0
                ? `${mobileStyles?.borderBottomRightRadius}px`
                : `${mobileStyles?.borderRadius}px`,
        borderBottomLeftRadius:
            mobileStyles?.borderBottomLeftRadius || mobileStyles?.borderBottomLeftRadius === 0
                ? `${mobileStyles?.borderBottomLeftRadius}px`
                : `${mobileStyles?.borderRadius}px`,
    };

    let columnBg: JssStyle = {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        zIndex: -1,
        display: 'block',
        background: websiteColorCss(styles.backgroundColor as string),
        ...borderRadiusStyles,
    };

    const columnWidgetsList: JssStyle = {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        justifyContent: `${verticalPositionMatrix[data.verticalAlign]}`,
        // '& div[class^="imgWidgetWrapper"]': {
        //     justifyContent: `${horizontalPositionMatrix[data.horizontalAlign]}`,
        // },
        // '& div[class^="button-group"]': {
        //     justifyContent: `${horizontalPositionMatrix[data.horizontalAlign]}`,
        // },
    };

    if (data.isShadow) {
        columnBg.boxShadow = boxShadowValue;
    }

    if (data.isBorder === false) {
        columnBg.border = 'none';
    }

    if (data.isColorOverflow) {
        let rgba = getRgbaColor(data.colorOverflow, data.colorOverflowOpacity);
        colorOverflow = `linear-gradient(${rgba}, ${rgba}), `;
    }

    if (data.backgroundImage) {
        columnBg.background = `${colorOverflow}${encodeFilePreviewUrlWithResize(data.backgroundImage, fileManagerCdnUrl)}, ${
            columnBg.background
        }`;
        columnBg.backgroundPosition = styles.backgroundPosition;
        columnBg.backgroundRepeat = styles.backgroundRepeat;
        columnBg.backgroundSize = styles.backgroundSize;

        if (data.isFilterOverlay) {
            columnBg.filter = data.filterOverlay;

            if (data.filterOverlay === EFilerOverlay.BLURRY) {
                columnBg.margin = '-3px';
            }
        }
    }

    const columnInner: JssStyle = {
        paddingTop: `${styles.paddingTop}px`,
        paddingBottom: `${styles.paddingBottom}px`,
        paddingLeft: `${styles.paddingLeft}px`,
        paddingRight: `${styles.paddingRight}px`,
        height: '100%',
        ...borderRadiusStyles,
    };

    if (data.isBorder) {
        columnInner.borderTopWidth =
            (styles?.borderTopWidth || styles?.borderTopWidth === 0 ? styles?.borderTopWidth : styles.borderWidth) + 'px';
        columnInner.borderTopStyle = styles?.borderTopStyle ? styles?.borderTopStyle : styles.borderStyle;
        columnInner.borderTopColor = styles?.borderTopColor ? styles?.borderTopColor : styles.borderColor;
        columnInner.borderRightWidth =
            (styles?.borderRightWidth || styles?.borderRightWidth === 0 ? styles?.borderRightWidth : styles.borderWidth) + 'px';
        columnInner.borderRightStyle = styles?.borderRightStyle ? styles?.borderRightStyle : styles.borderStyle;
        columnInner.borderRightColor = styles?.borderRightColor ? styles?.borderRightColor : styles.borderColor;
        columnInner.borderBottomWidth =
            (styles?.borderBottomWidth || styles?.borderBottomWidth === 0 ? styles?.borderBottomWidth : styles.borderWidth) + 'px';
        columnInner.borderBottomStyle = styles?.borderBottomStyle ? styles?.borderBottomStyle : styles.borderStyle;
        columnInner.borderBottomColor = styles?.borderBottomColor ? styles?.borderBottomColor : styles.borderColor;
        columnInner.borderLeftWidth =
            (styles?.borderLeftWidth || styles?.borderLeftWidth === 0 ? styles?.borderLeftWidth : styles.borderWidth) + 'px';
        columnInner.borderLeftStyle = styles?.borderLeftStyle ? styles?.borderLeftStyle : styles.borderStyle;
        columnInner.borderLeftColor = styles?.borderLeftColor ? styles?.borderLeftColor : styles.borderColor;
    }

    if (mobileStyles) {
        columnBg['@media (max-width: 992px)'] = {
            background: websiteColorCss(mobileStyles.backgroundColor as string),
            ...mobileBorderRadiusStyles,
        };

        columnBg['&.mobileColumnBg'] = {
            background: websiteColorCss(mobileStyles.backgroundColor as string),
            ...mobileBorderRadiusStyles,
        };

        columnInner['@media (max-width: 992px)'] = {
            paddingTop: `${mobileStyles.paddingTop}px`,
            paddingBottom: `${mobileStyles.paddingBottom}px`,
            paddingLeft: `${mobileStyles.paddingLeft}px`,
            paddingRight: `${mobileStyles.paddingRight}px`,
            ...mobileBorderRadiusStyles,
        };

        if (isConstructor) {
            columnBg['&.mobileColumnViewWrap'] = {
                background: websiteColorCss(mobileStyles.backgroundColor as string),
                ...mobileBorderRadiusStyles,
            };

            columnInner['&.mobileColumnViewInner'] = {
                paddingTop: `${mobileStyles.paddingTop}px`,
                paddingBottom: `${mobileStyles.paddingBottom}px`,
                paddingLeft: `${mobileStyles.paddingLeft}px`,
                paddingRight: `${mobileStyles.paddingRight}px`,
                ...mobileBorderRadiusStyles,
            };
        }
    }

    if (!mobileStyles) {
        columnInner['@media (max-width: 992px)'] = {
            paddingLeft: '15px',
            paddingRight: '15px',
        };

        if (isConstructor) {
            columnInner['&.mobileColumnViewInner'] = {
                paddingLeft: '15px',
                paddingRight: '15px',
            };
        }
    }

    if (mobileStyles && mobileData) {
        if (mobileData.backgroundImage) {
            if (!data.isSynchronizeStyle) {
                if (mobileData.colorOverflow && mobileData.isColorOverflow) {
                    let rgba = getRgbaColor(mobileData.colorOverflow, mobileData.colorOverflowOpacity);
                    colorOverflow = `linear-gradient(${rgba}, ${rgba}), `;
                } else {
                    colorOverflow = '';
                }

                if (mobileData.isFilterOverlay) {
                    columnBg['@media (max-width: 992px)'].filter = mobileData.filterOverlay;

                    if (mobileData.filterOverlay === EFilerOverlay.BLURRY) {
                        columnBg['@media (max-width: 992px)'].margin = '-3px';
                    }
                }
            }

            columnBg['@media (max-width: 992px)'] = {
                ...columnBg['@media (max-width: 992px)'],
                background: `${colorOverflow}${encodeFilePreviewUrlWithResize(mobileData.backgroundImage, fileManagerCdnUrl)}`,
                backgroundPosition: mobileStyles.backgroundPosition,
                backgroundRepeat: mobileStyles.backgroundRepeat,
                backgroundSize: mobileStyles.backgroundSize,
                backgroundColor: websiteColorCss(mobileStyles.backgroundColor as string),
            };
        }

        if (isConstructor && mobileData.backgroundImage) {
            if (!data.isSynchronizeStyle) {
                if (mobileData.colorOverflow && mobileData.isColorOverflow) {
                    let rgba = getRgbaColor(mobileData.colorOverflow, mobileData.colorOverflowOpacity);
                    colorOverflow = `linear-gradient(${rgba}, ${rgba}), `;
                } else {
                    colorOverflow = '';
                }

                if (mobileData.isFilterOverlay) {
                    columnBg['&.mobileColumnBg'].filter = mobileData.filterOverlay;

                    if (mobileData.filterOverlay === EFilerOverlay.BLURRY) {
                        columnBg['&.mobileColumnBg'].margin = '-3px';
                    }
                }
            }

            columnBg['&.mobileColumnBg'] = {
                ...columnBg['&.mobileColumnBg'],
                background: `${colorOverflow}${encodeFilePreviewUrlWithResize(mobileData.backgroundImage, fileManagerCdnUrl)}`,
                backgroundPosition: mobileStyles.backgroundPosition,
                backgroundRepeat: mobileStyles.backgroundRepeat,
                backgroundSize: mobileStyles.backgroundSize,
                backgroundColor: websiteColorCss(mobileStyles.backgroundColor as string),
            };
        }
    }

    if (mobileData?.isBorder) {
        columnInner['@media (max-width: 992px)'] = {
            ...columnInner,
            borderTopWidth: (mobileStyles?.borderTopWidth ? mobileStyles?.borderTopWidth : styles.borderWidth) + 'px',
            borderTopStyle: mobileStyles?.borderTopStyle ? mobileStyles?.borderTopStyle : styles.borderStyle,
            borderTopColor: mobileStyles?.borderTopColor ? mobileStyles?.borderTopColor : styles.borderColor,
            borderRightWidth: (mobileStyles?.borderRightWidth ? mobileStyles?.borderRightWidth : styles.borderWidth) + 'px',
            borderRightStyle: mobileStyles?.borderRightStyle ? mobileStyles?.borderRightStyle : styles.borderStyle,
            borderRightColor: mobileStyles?.borderRightColor ? mobileStyles?.borderRightColor : styles.borderColor,
            borderBottomWidth: (mobileStyles?.borderBottomWidth ? mobileStyles?.borderBottomWidth : styles.borderWidth) + 'px',
            borderBottomStyle: mobileStyles?.borderBottomStyle ? mobileStyles?.borderBottomStyle : styles.borderStyle,
            borderBottomColor: mobileStyles?.borderBottomColor ? mobileStyles?.borderBottomColor : styles.borderColor,
            borderLeftWidth: (mobileStyles?.borderLeftWidth ? mobileStyles?.borderLeftWidth : styles.borderWidth) + 'px',
            borderLeftStyle: mobileStyles?.borderLeftStyle ? mobileStyles?.borderLeftStyle : styles.borderStyle,
            borderLeftColor: mobileStyles?.borderLeftColor ? mobileStyles?.borderLeftColor : styles.borderColor,
        };

        columnInner['&.mobileColumnViewInner'] = {
            ...columnInner,
            borderTopWidth: (mobileStyles?.borderTopWidth ? mobileStyles?.borderTopWidth : styles.borderWidth) + 'px',
            borderTopStyle: mobileStyles?.borderTopStyle ? mobileStyles?.borderTopStyle : styles.borderStyle,
            borderTopColor: mobileStyles?.borderTopColor ? mobileStyles?.borderTopColor : styles.borderColor,
            borderRightWidth: (mobileStyles?.borderRightWidth ? mobileStyles?.borderRightWidth : styles.borderWidth) + 'px',
            borderRightStyle: mobileStyles?.borderRightStyle ? mobileStyles?.borderRightStyle : styles.borderStyle,
            borderRightColor: mobileStyles?.borderRightColor ? mobileStyles?.borderRightColor : styles.borderColor,
            borderBottomWidth: (mobileStyles?.borderBottomWidth ? mobileStyles?.borderBottomWidth : styles.borderWidth) + 'px',
            borderBottomStyle: mobileStyles?.borderBottomStyle ? mobileStyles?.borderBottomStyle : styles.borderStyle,
            borderBottomColor: mobileStyles?.borderBottomColor ? mobileStyles?.borderBottomColor : styles.borderColor,
            borderLeftWidth: (mobileStyles?.borderLeftWidth ? mobileStyles?.borderLeftWidth : styles.borderWidth) + 'px',
            borderLeftStyle: mobileStyles?.borderLeftStyle ? mobileStyles?.borderLeftStyle : styles.borderStyle,
            borderLeftColor: mobileStyles?.borderLeftColor ? mobileStyles?.borderLeftColor : styles.borderColor,
        };
    } else {
        columnInner['@media (max-width: 992px)'] = {
            ...columnInner['@media (max-width: 992px)'],
            border: 'none !important',
        };

        columnInner['&.mobileColumnViewInner'] = {
            ...columnInner['&.mobileColumnViewInner'],
            border: 'none !important',
        };
    }

    const columnAnimationPreset = {
        opacity: data.animation.isActive && !isPreview ? 0 : 1,
        height: '100%',
    };

    return {
        columnWrapper,
        columnBg,
        columnInner,
        columnWidgetsList,
        columnAnimationPreset,
    };
}
